import type {
  AdminWithdrawal,
  AutoWithdrawalsStatus,
  Asset,
  AuthResponse,
  CopySubscription,
  CurrentUser,
  HealthReport,
  KycApplication,
  MasterEquityCurve,
  MasterEquityRecompute,
  MasterStats,
  MasterTrader,
  Market,
  Network,
  Paginated,
  User,
  WithdrawalStatus,
  Locale,
} from '@app/shared'

import { ApiError, api } from '../lib/http'
import { clearTokens, setTokens } from '../lib/session'

/**
 * The operator's API. Decisions that move money or change what a user sees
 * take a `totpCode`; the caller collects it right before the call.
 */

export async function signIn(email: string, password: string, totpCode?: string): Promise<AuthResponse> {
  const result = await api.post<AuthResponse>('/auth/login', { email, password, ...(totpCode ? { totpCode } : {}) }, { anonymous: true })
  setTokens(result.tokens)
  return result
}

export async function signOut(): Promise<void> {
  try {
    await api.post<void>('/auth/logout')
  } finally {
    clearTokens()
  }
}

export function getCurrentUser(): Promise<CurrentUser> {
  return api.get<CurrentUser>('/users/me')
}

export function getHealth(): Promise<HealthReport> {
  return api.get<HealthReport>('/health/ready', { anonymous: true, root: true })
}

export function getNetworks(): Promise<Network[]> {
  return api.get<Network[]>('/networks', { anonymous: true })
}

export function getAssets(): Promise<Asset[]> {
  return api.get<Asset[]>('/assets', { anonymous: true })
}

/** What the apps read before sign-in; any desk may ask (the settings list needs `settings`). */
export interface PlatformConfig {
  /** `auth.invitation_required`. */
  invitationRequired: boolean
  signalsChannelUrl: string | null
  supportEmail: string
  /** IANA zone the channel and the app write signal times in (`signals.display_timezone`). */
  timezone: string
  /**
   * How long a signal code lives after its release unless the signal is given
   * its own length, whole minutes (`signals.code_ttl_minutes`, 5 by default).
   * Absent on a server older than the five-minute rule: read it through
   * `signalCodeTtlMinutes()`.
   */
  signalCodeTtlMinutes?: number
}

export function getPlatformConfig(): Promise<PlatformConfig> {
  return api.get<PlatformConfig>('/platform/config', { anonymous: true })
}

/* ---------------------------------- overview ---------------------------------- */

export interface Overview {
  pendingWithdrawals: number
  approvedWithdrawals: number
  /** Hot-wallet sends that failed or are stuck: an operator must act. */
  withdrawalsNeedingAttention: number
  pendingKyc: number
  /** Credited deposits whose transaction left the chain, waiting on a decision. */
  pendingDepositReversals: number
  users: number
  activeSubscriptions: number
  mastersRunning: number
  copyAum: string
  insuranceFund: string
  openFuturesPositions: number
}

export function getOverview(): Promise<Overview> {
  return api.get<Overview>('/admin/overview')
}

/* --------------------------------- withdrawals -------------------------------- */

export function getWithdrawals(status?: WithdrawalStatus, page = 1): Promise<Paginated<AdminWithdrawal>> {
  return api.get<Paginated<AdminWithdrawal>>('/admin/withdrawals', { query: { status, page, pageSize: 50 } })
}

export function approveWithdrawal(id: string, totpCode: string): Promise<AdminWithdrawal> {
  return api.post<AdminWithdrawal>(`/admin/withdrawals/${id}/approve`, undefined, { totpCode })
}

export function rejectWithdrawal(id: string, reason: string, totpCode: string): Promise<AdminWithdrawal> {
  return api.post<AdminWithdrawal>(`/admin/withdrawals/${id}/reject`, { reason }, { totpCode })
}

export function sendWithdrawal(id: string, txHash: string | undefined, totpCode: string): Promise<AdminWithdrawal> {
  return api.post<AdminWithdrawal>(`/admin/withdrawals/${id}/send`, txHash ? { txHash } : {}, { totpCode })
}

export function failWithdrawal(id: string, reason: string, totpCode: string): Promise<AdminWithdrawal> {
  return api.post<AdminWithdrawal>(`/admin/withdrawals/${id}/fail`, { reason }, { totpCode })
}

/** Automatic withdrawals: today's budget and which chain families the hot wallets sign for. */
export function getAutoWithdrawalsStatus(): Promise<AutoWithdrawalsStatus> {
  return api.get<AutoWithdrawalsStatus>('/admin/withdrawals/auto')
}

/* ------------------------------------- kyc ------------------------------------ */

export interface KycQueueItem extends KycApplication {
  documentNumberMasked: string | null
  userEmail: string | null
}

export function getKycQueue(status?: string, page = 1): Promise<Paginated<KycQueueItem>> {
  return api.get<Paginated<KycQueueItem>>('/admin/kyc', { query: { status, page, pageSize: 50 } })
}

export function reviewKyc(
  id: string,
  status: 'in_review' | 'approved' | 'rejected' | 'resubmit',
  rejectionReason?: string,
): Promise<KycQueueItem> {
  return api.patch<KycQueueItem>(`/kyc/applications/${id}/status`, {
    status,
    ...(rejectionReason ? { rejectionReason } : {}),
  })
}

export function kycDocumentUrl(documentId: string): string {
  return `/kyc/documents/${documentId}/file`
}

/* ------------------------------------ users ----------------------------------- */

export interface AdminUser extends User {
  kycStatus: string | null
  spotUsdt: string
}

export function getUsers(query: { search?: string; status?: string; role?: string; page?: number }): Promise<Paginated<AdminUser>> {
  return api.get<Paginated<AdminUser>>('/admin/users', { query: { ...query, pageSize: 50 } })
}

export function patchUser(
  id: string,
  patch: { status?: 'active' | 'suspended' | 'banned'; role?: 'user' | 'admin' | 'support' | 'finance' | 'content'; reason?: string },
  totpCode: string,
): Promise<AdminUser> {
  return api.patch<AdminUser>(`/admin/users/${id}`, patch, { totpCode })
}

/* ----------------------------------- masters ---------------------------------- */

export interface Strategy {
  masterId: string
  status: 'stopped' | 'running' | 'paused' | 'error'
  marketIds: number[]
  marketType: 'spot' | 'futures'
  sideBias: 'long_only' | 'short_only' | 'both'
  leverage: number
  issueSignals: boolean
  signalAccessKind: SignalAccessKind
  signalWindowSec: number
  positionFraction: string
  maxOpenPositions: number
  minIntervalSec: number
  maxIntervalSec: number
  takeProfitPct: string | null
  stopLossPct: string | null
  maxHoldSec: number | null
  randomSeed: string
  targetWinRate: string | null
  lastActionAt: string | null
  nextActionAt: string | null
  lastError: string | null
}

export interface StrategyInput {
  marketIds: number[]
  marketType: 'spot' | 'futures'
  sideBias: 'long_only' | 'short_only' | 'both'
  leverage: number
  issueSignals: boolean
  signalAccessKind: SignalAccessKind
  signalWindowSec: number
  positionFraction: string
  maxOpenPositions: number
  minIntervalSec: number
  maxIntervalSec: number
  takeProfitPct: string | null
  stopLossPct: string | null
  maxHoldSec: number | null
  randomSeed: string
  targetWinRate: string | null
}

export interface AdminMaster extends MasterTrader {
  email: string
  strategyStatus: string | null
  computedStats: MasterStats | null
  strategy: Strategy | null
}

export interface DisplayStatsInput {
  roi7d?: string | null
  roi30d?: string | null
  roiAll?: string | null
  pnl30d?: string | null
  winRate?: string | null
  totalTrades?: number | null
  maxDrawdown?: string | null
  riskLevel?: 'low' | 'medium' | 'high' | null
  followersCount?: number | null
  aum?: string | null
  periodStart?: string | null
}

export function getMasters(): Promise<AdminMaster[]> {
  return api.get<AdminMaster[]>('/admin/masters')
}

export function createMaster(
  body: {
    email: string
    displayName: string
    bio?: string | null
    tags?: string[]
    minCopyAmount?: string
    commissionRate?: string
    strategy: StrategyInput
    strategyStatus?: 'stopped' | 'running'
  },
  totpCode: string,
): Promise<AdminMaster> {
  return api.post<AdminMaster>('/admin/masters', body, { totpCode })
}

export function patchMaster(
  id: string,
  patch: { displayName?: string; bio?: string | null; tags?: string[]; minCopyAmount?: string; commissionRate?: string; isPublic?: boolean; status?: 'pending' | 'active' | 'paused' | 'blocked' },
): Promise<AdminMaster> {
  return api.patch<AdminMaster>(`/admin/masters/${id}`, patch)
}

export function putStrategy(id: string, strategy: StrategyInput, status: 'stopped' | 'running' | 'paused'): Promise<Strategy> {
  return api.put<Strategy>(`/admin/masters/${id}/strategy`, { strategy, status })
}

export function setStrategyStatus(id: string, status: 'stopped' | 'running' | 'paused'): Promise<Strategy> {
  return api.post<Strategy>(`/admin/masters/${id}/strategy/status`, { status })
}

export function putDisplayStats(id: string, stats: DisplayStatsInput, totpCode: string): Promise<AdminMaster> {
  return api.put<AdminMaster>(`/admin/masters/${id}/display-stats`, stats, { totpCode })
}

export function clearDisplayStats(id: string, totpCode: string): Promise<AdminMaster> {
  return api.delete<AdminMaster>(`/admin/masters/${id}/display-stats`, { totpCode })
}

export function getMasterPositions(id: string): Promise<CopySubscription[]> {
  return api.get<CopySubscription[]>(`/copy/masters/${id}/positions`)
}

/** The honest daily equity curve, oldest first, with the Sharpe ratio drawn from it. */
export function getMasterEquity(id: string): Promise<MasterEquityCurve> {
  return api.get<MasterEquityCurve>(`/admin/masters/${id}/equity`)
}

/**
 * Rebuilds one master's past days from its books and restates its Sharpe
 * ratio now. Never takes today's point — only the server's clock does.
 */
export function recomputeMasterEquity(id: string): Promise<MasterEquityRecompute> {
  return api.post<MasterEquityRecompute>(`/admin/masters/${id}/equity/recompute`)
}

/* ----------------------------------- registry --------------------------------- */

export interface RegistryNetwork {
  id: number
  code: string
  name: string
  family: string
  isEnabled: boolean
  confirmationsRequired: number
  hasRpcUrl: boolean
  /** A deposit watcher in this build can read the network; without one users are never offered it. */
  watcherAvailable: boolean
  assets: Array<{ symbol: string; depositEnabled: boolean; withdrawEnabled: boolean; minDeposit: string; minWithdraw: string; withdrawFee: string }>
}

export interface RegistryMarket {
  id: number
  symbol: string
  marketType: string
  isEnabled: boolean
  makerFee: string
  takerFee: string
  maxLeverage: number
  minNotional: string
  maintenanceMarginRate: string
}

export function getRegistryNetworks(): Promise<RegistryNetwork[]> {
  return api.get<RegistryNetwork[]>('/admin/networks')
}

export function patchNetwork(code: string, patch: { isEnabled?: boolean; confirmationsRequired?: number }, totpCode: string): Promise<RegistryNetwork[]> {
  return api.patch<RegistryNetwork[]>(`/admin/networks/${code}`, patch, { totpCode })
}

export function patchNetworkAsset(
  code: string,
  symbol: string,
  patch: { depositEnabled?: boolean; withdrawEnabled?: boolean; minDeposit?: string; minWithdraw?: string; withdrawFee?: string },
  totpCode: string,
): Promise<RegistryNetwork[]> {
  return api.patch<RegistryNetwork[]>(`/admin/networks/${code}/assets/${symbol}`, patch, { totpCode })
}

export function getRegistryMarkets(): Promise<RegistryMarket[]> {
  return api.get<RegistryMarket[]>('/admin/markets')
}

export function patchMarket(
  symbol: string,
  patch: { isEnabled?: boolean; makerFee?: string; takerFee?: string; maxLeverage?: number; minNotional?: string; maintenanceMarginRate?: string },
  totpCode: string,
): Promise<RegistryMarket[]> {
  return api.patch<RegistryMarket[]>(`/admin/markets/${encodeURIComponent(symbol)}`, patch, { totpCode })
}

export function setSpotFees(fees: { spotTakerFee?: string; spotMakerFee?: string }, totpCode: string): Promise<RegistryMarket[]> {
  return api.put<RegistryMarket[]>('/admin/markets/fees', fees, { totpCode })
}

/* ----------------------------------- settings --------------------------------- */

export interface Setting {
  key: string
  value: unknown
  description: string | null
  updatedBy: string | null
  updatedAt: string | null
  isDefault: boolean
}

export function getSettings(): Promise<Setting[]> {
  return api.get<Setting[]>('/admin/settings')
}

export function putSetting(key: string, value: unknown, totpCode: string, description?: string): Promise<Setting> {
  return api.put<Setting>(`/admin/settings/${key}`, { value, ...(description ? { description } : {}) }, { totpCode })
}

/* ------------------------------------ audit ----------------------------------- */

export interface AuditEntry {
  id: string
  actorId: string | null
  actorEmail: string | null
  actorRole: string | null
  action: string
  entityType: string | null
  entityId: string | null
  before: unknown
  after: unknown
  ip: string | null
  userAgent: string | null
  createdAt: string
}

export function getAudit(query: { page?: number; entityType?: string; entityId?: string; action?: string }): Promise<Paginated<AuditEntry>> {
  return api.get<Paginated<AuditEntry>>('/admin/audit', { query: { ...query, pageSize: 50 } })
}

/* -------------------------------- announcements ------------------------------- */

export interface Announcement {
  id: string
  kind: 'ticker' | 'banner'
  locale: Locale
  title: string | null
  body: string
  ctaLabel: string | null
  ctaTo: string | null
  art: 'copy' | 'markets' | 'deposit' | null
  sortOrder: number
  isEnabled: boolean
  startsAt: string | null
  endsAt: string | null
  createdAt: string
  updatedAt: string
}

export type AnnouncementInput = Omit<Announcement, 'id' | 'createdAt' | 'updatedAt'>

export function getAnnouncements(): Promise<Announcement[]> {
  return api.get<Announcement[]>('/admin/announcements')
}

export function createAnnouncement(body: AnnouncementInput): Promise<Announcement> {
  return api.post<Announcement>('/admin/announcements', body)
}

export function updateAnnouncement(id: string, patch: Partial<AnnouncementInput>): Promise<Announcement> {
  return api.patch<Announcement>(`/admin/announcements/${id}`, patch)
}

export function deleteAnnouncement(id: string): Promise<void> {
  return api.delete<void>(`/admin/announcements/${id}`)
}

/* ----------------------------------- security --------------------------------- */

export interface TotpStatus {
  enabled: boolean
  pending: boolean
}

export function getTotpStatus(): Promise<TotpStatus> {
  return api.get<TotpStatus>('/users/me/totp')
}

export function beginTotpSetup(): Promise<{ secret: string; otpauthUrl: string }> {
  return api.post<{ secret: string; otpauthUrl: string }>('/users/me/totp/setup')
}

export function enableTotp(code: string): Promise<TotpStatus> {
  return api.post<TotpStatus>('/users/me/totp/enable', { code })
}

export function disableTotp(code: string): Promise<TotpStatus> {
  return api.post<TotpStatus>('/users/me/totp/disable', { code })
}

/* ---------------------------------- signals ----------------------------------- */

export type SignalAccessKind = 'public' | 'base' | 'premium' | 'bonus' | 'agent'
export type SignalStatus = 'draft' | 'scheduled' | 'live' | 'cancelled' | 'expired'

export interface AdminSignal {
  id: string
  code: string
  title: string
  marketId: number
  marketSymbol: string
  /** `futures`: executed by the futures engine — `buy` is a long, `sell` a short, the amount is margin. */
  marketType: 'spot' | 'futures'
  /** 1 on spot. */
  leverage: number
  side: 'buy' | 'sell'
  releaseAt: string
  expiresAt: string
  defaultFraction: string
  minAmount: string | null
  maxAmount: string | null
  accessKind: SignalAccessKind
  /** Lowest partner level that may execute it; 0 opens it to everyone. */
  minLevelRank: number
  targetProfitPct: string | null
  takeProfitPct: string | null
  stopLossPct: string | null
  maxHoldSec: number | null
  status: SignalStatus
  createdBy: string | null
  publishedAt: string | null
  cancelledAt: string | null
  /** When the bot posted the code to the Telegram channel; null — not posted (yet). */
  channelPostedAt: string | null
  /** Why the last post to the channel failed, in the bot's words. */
  channelError: string | null
  createdAt: string
  updatedAt: string
}

export interface SignalInput {
  code: string
  title: string
  marketId: number
  side: 'buy' | 'sell'
  /** 1…the market's maximum on futures; always 1 on spot. */
  leverage: number
  releaseAt: string
  /**
   * The release plus the length chosen in the form. Without it the server
   * ends the window `signals.code_ttl_minutes` after the release; longer than
   * a day it refuses (SIGNAL_WINDOW_TOO_LONG).
   */
  expiresAt?: string
  defaultFraction: string
  minAmount: string | null
  maxAmount: string | null
  accessKind: SignalAccessKind
  /** «0+» — everyone; raise it to open a signal only from that level up. */
  minLevelRank: number
  targetProfitPct: string | null
  takeProfitPct: string | null
  stopLossPct: string | null
  maxHoldSec: number | null
}

export function getSignals(query: { page?: number; status?: SignalStatus; accessKind?: SignalAccessKind }): Promise<Paginated<AdminSignal>> {
  return api.get<Paginated<AdminSignal>>('/admin/signals', { query: { ...query, pageSize: 50 } })
}

export function createSignal(body: SignalInput): Promise<AdminSignal> {
  return api.post<AdminSignal>('/admin/signals', body)
}

/** A `releaseAt` without `expiresAt` makes the server recompute the end from the new release. */
export function updateSignal(id: string, patch: Partial<SignalInput>): Promise<AdminSignal> {
  return api.patch<AdminSignal>(`/admin/signals/${id}`, patch)
}

export function deleteSignal(id: string): Promise<void> {
  return api.delete<void>(`/admin/signals/${id}`)
}

export function publishSignal(id: string, totpCode: string): Promise<AdminSignal> {
  return api.post<AdminSignal>(`/admin/signals/${id}/publish`, undefined, { totpCode })
}

export function cancelSignal(id: string, totpCode: string): Promise<AdminSignal> {
  return api.post<AdminSignal>(`/admin/signals/${id}/cancel`, undefined, { totpCode })
}

/** Posts (or re-posts) the signal's code and terms to the Telegram channel. */
export function postSignalToChannel(id: string, totpCode: string): Promise<{ postedAt: string; messageId: number | string }> {
  return api.post<{ postedAt: string; messageId: number | string }>(`/admin/signals/${id}/channel-post`, undefined, { totpCode })
}

export interface TelegramChannelCheck {
  ok: true
  chat: { id: number | string; title: string | null; type: string }
}

/** Asks Telegram whether the bot can post to `telegram.signals_channel`; 400 `TELEGRAM_CHANNEL_UNREACHABLE` says why not. */
export function testTelegramChannel(): Promise<TelegramChannelCheck> {
  return api.post<TelegramChannelCheck>('/admin/telegram/channel-test')
}

/** A market a signal can run on: every enabled spot and futures market. */
export interface SignalMarket {
  id: number
  symbol: string
  marketType: 'spot' | 'futures'
  maxLeverage: number
}

/**
 * The registry when this desk may read it (it shows changes at once); the
 * public list otherwise — the content desk runs signals without `registry`.
 * Both come back as enabled markets only.
 */
export async function getSignalMarkets(): Promise<SignalMarket[]> {
  const normalize = (market: { id: number; symbol: string; marketType: string; maxLeverage: number }): SignalMarket => ({
    id: market.id,
    symbol: market.symbol,
    marketType: market.marketType === 'futures' ? 'futures' : 'spot',
    maxLeverage: Math.max(1, Math.floor(Number(market.maxLeverage) || 1)),
  })
  try {
    const registry = await getRegistryMarkets()
    return registry.filter((market) => market.isEnabled && (market.marketType === 'spot' || market.marketType === 'futures')).map(normalize)
  } catch (error) {
    if (!(error instanceof ApiError) || error.status !== 403) throw error
    const open = await api.get<Paginated<Market>>('/markets', { anonymous: true })
    return open.items.filter((market) => market.isEnabled && (market.marketType === 'spot' || market.marketType === 'futures')).map(normalize)
  }
}

/* -------------------------------- invite codes -------------------------------- */

/** An operator's invitation: registration by it has no referrer. */
export interface InviteCode {
  id: string
  code: string
  label: string
  maxUses: number | null
  uses: number
  expiresAt: string | null
  isActive: boolean
  revokedAt: string | null
  createdAt: string
  createdBy: { id: string; email: string } | null
}

export interface InviteCodeInput {
  /** Empty — the server generates one. */
  code?: string
  label: string
  maxUses?: number | null
  expiresAt?: string | null
}

export function getInviteCodes(): Promise<{ items: InviteCode[] }> {
  return api.get<{ items: InviteCode[] }>('/admin/invite-codes')
}

export function createInviteCode(body: InviteCodeInput): Promise<InviteCode> {
  return api.post<InviteCode>('/admin/invite-codes', body)
}

export function revokeInviteCode(id: string): Promise<InviteCode> {
  return api.post<InviteCode>(`/admin/invite-codes/${id}/revoke`)
}

/* --------------------------------- referrals ---------------------------------- */

export interface ReferralProgram {
  enabled: boolean
  qualificationAssetSymbol: string
  qualificationDepositUsdt: string
  qualificationRequiresKyc: boolean
  welcomeSignalGrants: number
  referrerSignalGrants: number
  promotionalRewardUsdt: string
  promoTurnoverMultiple: string
  promoValidDays: number
  qualificationHoldDays: number
  antiabuseEnabled: boolean
  secondLevelShareRate: string
  feeRebatesEnabled: boolean
  rewardReviewRequired: boolean
  depositTiersEnabled: boolean
  qualificationMinReferrerBalanceShare: string
  topupWindowDays: number
  promotionBonusesEnabled: boolean
  premiumSignalMinDirects: number
  rebateBasis: 'actual_collected_trading_fee'
}

/** One band of the deposit table: percentages for the inviter and the newcomer. */
export interface ReferralDepositTier {
  id: number
  rank: number
  name: string
  minDepositUsdt: string
  maxDepositUsdt: string | null
  referrerRate: string
  welcomeRate: string
  isActive: boolean
}

export type ReferralDepositTierInput = Omit<ReferralDepositTier, 'id' | 'rank' | 'isActive'>

export type ReferralQualificationStatus = 'qualified' | 'flagged' | 'revoked'

export interface AdminReferralQualification {
  id: string
  referredUserId: string
  referrerUserId: string
  referredEmail: string
  referrerEmail: string
  sourceType: string
  sourceId: string
  qualifyingAmountUsdt: string
  status: ReferralQualificationStatus
  qualifiedAt: string
  revokedAt: string | null
  revocationReason: string | null
  riskFlags: string[]
}

export function getReferralQualifications(query: { page?: number; status?: ReferralQualificationStatus }): Promise<Paginated<AdminReferralQualification>> {
  return api.get<Paginated<AdminReferralQualification>>('/admin/referrals/qualifications', { query: { page: query.page ?? 1, pageSize: 50, status: query.status } })
}

export function reviewReferralQualification(id: string, body: { decision: 'approve' | 'reject'; reason: string }, totpCode: string): Promise<AdminReferralQualification> {
  return api.patch<AdminReferralQualification>(`/admin/referrals/qualifications/${id}`, body, { totpCode })
}

export interface PartnerLevel {
  id: number
  code: string
  rank: number
  name: string
  /** Qualified direct referrals that reach this level; `null` — not that way. */
  minQualifiedDirects: number | null
  /** Credited deposits in USDT that reach this level alone; `null` — not that way. */
  minDepositUsdt: string | null
  feeShareRate: string
  weeklySignalGrants: number
  promotionBonusUsdt: string
}

export interface ReferralSettings {
  program: ReferralProgram
  levels: PartnerLevel[]
  depositTiers: ReferralDepositTier[]
  updatedBy: string | null
  updatedAt: string
}

export type ReferralSettingsPatch = Partial<Omit<ReferralProgram, 'rebateBasis'>>

export type ReferralRewardStatus = 'pending_review' | 'approved' | 'paid' | 'rejected' | 'reversed'
export type ReferralRewardKind =
  | 'fee_rebate'
  | 'signal_grant'
  | 'promotional_credit'
  | 'deposit_bonus'
  | 'welcome_bonus'
  | 'level_bonus'

export interface AdminReferralReward {
  id: string
  beneficiaryUserId: string
  beneficiaryEmail: string
  relatedUserId: string | null
  relatedUserEmail: string | null
  kind: ReferralRewardKind
  unit: 'USDT' | 'signal'
  amount: string
  basisAmountUsdt: string | null
  feeShareRate: string | null
  sourceType: string
  sourceId: string
  status: ReferralRewardStatus
  description: string
  reviewedBy: string | null
  reviewedAt: string | null
  reviewReason: string | null
  createdAt: string
  updatedAt: string
  payout: { id: string; status: 'paid' | 'failed' | 'reversed'; externalReference: string | null; paidAt: string | null } | null
}

export function getReferralSettings(): Promise<ReferralSettings> {
  return api.get<ReferralSettings>('/admin/referrals/settings')
}

export function patchReferralSettings(patch: ReferralSettingsPatch, totpCode: string): Promise<ReferralSettings> {
  return api.patch<ReferralSettings>('/admin/referrals/settings', patch, { totpCode })
}

/** Replaces the whole deposit table; the API validates it as one set. */
export function putReferralDepositTiers(tiers: ReferralDepositTierInput[], totpCode: string): Promise<ReferralSettings> {
  return api.put<ReferralSettings>('/admin/referrals/deposit-tiers', { tiers }, { totpCode })
}

export function getReferralRewards(query: { page?: number; status?: ReferralRewardStatus; kind?: ReferralRewardKind }): Promise<Paginated<AdminReferralReward>> {
  return api.get<Paginated<AdminReferralReward>>('/admin/referrals/rewards', { query: { ...query, pageSize: 50 } })
}

export function reviewReferralReward(
  id: string,
  body: { action: 'approve' | 'reject' | 'mark_paid' | 'reverse'; reason?: string; payoutReference?: string },
  totpCode: string,
): Promise<AdminReferralReward> {
  return api.patch<AdminReferralReward>(`/admin/referrals/rewards/${id}`, body, { totpCode })
}

/* ---------------------------------- reports ----------------------------------- */

export interface ReportSummary {
  from: string
  to: string
  trading: { trades: number; turnoverUsdt: string; feesUsdt: string; traders: number }
  deposits: { count: number; credited: Array<{ assetSymbol: string; networkCode: string; amount: string }> }
  withdrawals: { requested: number; sent: number; sentAmount: Array<{ assetSymbol: string; networkCode: string; amount: string; fee: string; platformFee: string }>; pending: number }
  partners: { qualifications: number; rewardsPendingUsdt: string; rewardsApprovedUsdt: string; rewardsPaidUsdt: string; promoUnlockedUsdt: string; promoLockedUsdt: string }
  futures: { positionsClosed: number; realizedPnlUsdt: string; fundingUsdt: string; liquidations: number; insuranceNetUsdt: string }
  copy: { subscriptionsStarted: number; subscriptionsStopped: number; activeNow: number; aumUsdt: string }
  users: { registered: number; verified: number; activeTraders: number }
}

export type ReportKind = 'trades' | 'withdrawals' | 'deposits' | 'ledger' | 'rewards' | 'insurance'

export type DuplicateKind = 'signup_device' | 'signup_ip' | 'login_ip'
export interface DuplicateGroup {
  kind: DuplicateKind
  key: string
  members: Array<{ userId: string; email: string; status: string; createdAt: string }>
}
export interface DuplicatesReport {
  generatedAt: string
  groups: DuplicateGroup[]
}
export function getDuplicatesReport(): Promise<DuplicatesReport> {
  return api.get<DuplicatesReport>('/admin/reports/duplicates')
}

export function getReportSummary(from: string, to: string): Promise<ReportSummary> {
  return api.get<ReportSummary>('/admin/reports/summary', { query: { from, to } })
}

/** The CSV as a blob: the panel triggers the browser download itself. */
export async function downloadReport(kind: ReportKind, from: string, to: string): Promise<{ blob: Blob; filename: string; rows: number }> {
  const response = await api.raw('/admin/reports/export', { query: { kind, from, to } })
  const disposition = response.headers.get('content-disposition') ?? ''
  const match = /filename="([^"]+)"/.exec(disposition)
  return { blob: await response.blob(), filename: match?.[1] ?? `${kind}.csv`, rows: Number(response.headers.get('x-row-count') ?? '0') }
}

/* ------------------------------ ledger & search ------------------------------- */

export interface AdminBalance {
  assetId: number
  assetSymbol: string
  accountType: 'spot' | 'futures' | 'copy'
  available: string
  locked: string
}

export interface AdminLedgerEntry {
  id: string
  accountType: 'spot' | 'futures' | 'copy'
  assetSymbol: string
  entryType: string
  amount: string
  balanceAfter: string
  refType: string | null
  refId: string | null
  metadata: Record<string, unknown> | null
  createdAt: string
}

export function getUserDetail(id: string): Promise<{ user: AdminUser; balances: AdminBalance[] }> {
  return api.get<{ user: AdminUser; balances: AdminBalance[] }>(`/admin/users/${id}/detail`)
}

export function getUserLedger(id: string, page = 1, accountType?: 'spot' | 'futures' | 'copy'): Promise<Paginated<AdminLedgerEntry>> {
  return api.get<Paginated<AdminLedgerEntry>>(`/admin/users/${id}/ledger`, { query: { page, pageSize: 50, accountType } })
}

/** `idempotencyKey` is minted per filled-in form: a retried submit after a lost response replays, never posts twice. */
export function adjustUserBalance(
  id: string,
  body: { assetSymbol: string; accountType: 'spot' | 'futures' | 'copy'; amount: string; reason: string },
  totpCode: string,
  idempotencyKey: string,
): Promise<{ entry: AdminLedgerEntry; balance: AdminBalance; replayed: boolean }> {
  return api.post<{ entry: AdminLedgerEntry; balance: AdminBalance; replayed: boolean }>(`/admin/users/${id}/adjustments`, body, { totpCode, idempotencyKey })
}

export interface SearchHit {
  kind: 'user' | 'withdrawal' | 'deposit' | 'signal'
  id: string
  title: string
  subtitle: string
  status: string | null
  userId: string | null
  userEmail: string | null
  createdAt: string
}

export function searchEverything(q: string): Promise<{ items: SearchHit[] }> {
  return api.get<{ items: SearchHit[] }>('/admin/search', { query: { q } })
}

/* ----------------------------------- alerts ----------------------------------- */

export interface SystemAlert {
  id: string
  checkKey: string
  severity: 'degraded' | 'error'
  message: string
  status: 'open' | 'resolved'
  openedAt: string
  notifiedAt: string | null
  resolvedAt: string | null
  lastSeenAt: string
}

export function getAlerts(): Promise<{ items: SystemAlert[] }> {
  return api.get<{ items: SystemAlert[] }>('/admin/alerts')
}

/* ------------------------------- futures desk ------------------------------ */

export interface AdminPosition {
  id: string
  userId: string
  userEmail: string
  marketId: number
  marketSymbol: string
  accountType: 'spot' | 'futures' | 'copy'
  side: 'long' | 'short'
  status: 'open' | 'closed' | 'liquidated'
  quantity: string
  entryPrice: string
  closePrice: string | null
  leverage: number
  margin: string
  marginMode: 'cross' | 'isolated'
  liquidationPrice: string | null
  realizedPnl: string
  unrealizedPnl: string | null
  markPrice: string | null
  fundingPaid: string
  feesPaid: string
  source: string
  openedAt: string
  closedAt: string | null
}

export function getOperatorPositions(status: 'open' | 'closed' | 'liquidated' | undefined, page = 1): Promise<Paginated<AdminPosition>> {
  return api.get<Paginated<AdminPosition>>('/admin/futures/positions', { query: { status, page, pageSize: 50 } })
}

export function liquidatePosition(
  id: string,
  body: { reason: string; price?: string },
  totpCode: string,
): Promise<{ position: AdminPosition; markPrice: string; priceSource: 'feed' | 'operator' }> {
  return api.post<{ position: AdminPosition; markPrice: string; priceSource: 'feed' | 'operator' }>(`/admin/futures/positions/${id}/liquidate`, body, { totpCode })
}

/* ------------------------------ deposit reversals ----------------------------- */

export type DepositReversalStatus = 'pending_review' | 'applied' | 'dismissed'

/** A credited deposit whose transaction later left the chain; see GET /admin/deposit-reversals. */
export interface AdminDepositReversal {
  id: string
  depositId: string
  userId: string
  userEmail: string
  assetId: number
  assetSymbol: string
  networkCode: string
  txHash: string
  explorerUrl: string | null
  address: string
  fromAddress: string | null
  blockNumber: string | null
  depositAmount: string
  depositStatus: string
  creditedAmount: string
  reversedAmount: string
  shortfall: string
  status: DepositReversalStatus
  /** What the watcher saw, in its own words. */
  reason: string
  /** First seen on chain. */
  detectedAt: string
  creditedAt: string | null
  /** Last acknowledged by the chain. */
  lastSeenAt: string | null
  /** When the watcher opened the review. */
  vanishedAt: string
  missingReceiptCount: number
  reviewedBy: string | null
  reviewedByEmail: string | null
  reviewedAt: string | null
  appliedAt: string | null
  /** The operator's reason. */
  notes: string | null
}

export interface DepositReversalProjection {
  available: string
  locked: string
  wouldReverse: string
  wouldShortfall: string
  sufficient: boolean
}

export interface AdminDepositReversalDetail {
  reversal: AdminDepositReversal
  balances: AdminBalance[]
  projection: DepositReversalProjection | null
  /** The user's withdrawals of this asset whose money has not left (pending, approved, broadcasting). Absent on an older API. */
  openWithdrawals?: { count: number; amount: string }
}

export interface DepositReversalDecision {
  reversal: AdminDepositReversal
  reversedAmount: string
  shortfall: string
  replayed: boolean
}

export function getDepositReversals(query: { status?: DepositReversalStatus; search?: string; page?: number }): Promise<Paginated<AdminDepositReversal>> {
  return api.get<Paginated<AdminDepositReversal>>('/admin/deposit-reversals', { query: { ...query, pageSize: 50 } })
}

export function getDepositReversal(id: string): Promise<AdminDepositReversalDetail> {
  return api.get<AdminDepositReversalDetail>(`/admin/deposit-reversals/${id}`)
}

/** `idempotencyKey` is minted when the decision dialog opens, so a retried click cannot debit twice. */
export function applyDepositReversal(id: string, reason: string, totpCode: string, idempotencyKey: string): Promise<DepositReversalDecision> {
  return api.post<DepositReversalDecision>(`/admin/deposit-reversals/${id}/apply`, { reason }, { totpCode, idempotencyKey })
}

export function dismissDepositReversal(id: string, reason: string, totpCode: string, idempotencyKey: string): Promise<DepositReversalDecision> {
  return api.post<DepositReversalDecision>(`/admin/deposit-reversals/${id}/dismiss`, { reason }, { totpCode, idempotencyKey })
}
